import React, { useState } from 'react';
import { Send, Search, Users, X, Check } from 'lucide-react';
import { ChatChannel, ChatMessage, User } from '../../../shared/types.ts';
import { Avatar } from '../common/Avatar.tsx';
import { Modal } from '../common/Modal.tsx';

interface ForwardMessageModalProps {
  isOpen: boolean;
  onClose: () => void;
  message: ChatMessage | null;
  channels: ChatChannel[];
  currentUser: User | null;
  onForward: (targetChannelId: string) => Promise<void>;
}

export const ForwardMessageModal: React.FC<ForwardMessageModalProps> = ({
  isOpen,
  onClose,
  message,
  channels,
  currentUser,
  onForward,
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedChannelId, setSelectedChannelId] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!isOpen || !message) return null;

  const filteredChannels = channels.filter((c) => {
    if (!searchQuery.trim()) return true;
    const q = searchQuery.toLowerCase();
    const nameMatch = c.name.toLowerCase().includes(q);
    const memberMatch = (c.members || []).some((m) => m?.fullName?.toLowerCase().includes(q));
    return nameMatch || memberMatch;
  });

  const handleSend = async () => {
    if (!selectedChannelId) return;
    setIsSubmitting(true);
    setError(null);
    try {
      await onForward(selectedChannelId);
      setSelectedChannelId(null);
      onClose();
    } catch (err: any) {
      console.error('Failed to forward message:', err);
      setError(err?.message || 'Could not forward the message. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Forward Message">
      <div className="space-y-4">
        {/* Forwarded Content Preview Box */}
        <div className="p-3 rounded-xl bg-surface-50 dark:bg-surface-800/60 border border-surface-200 dark:border-surface-700/60 text-xs">
          <span className="text-[10px] font-bold uppercase tracking-wider text-surface-400 block mb-1">
            Forwarding from {message.senderName}
          </span>
          <p className="line-clamp-2 text-surface-800 dark:text-surface-200 italic font-medium">
            "{message.content}"
          </p>
          {message.attachments && message.attachments.length > 0 && (
            <span className="text-[10px] text-brand-600 dark:text-brand-400 font-semibold mt-1 block">
              + {message.attachments.length} attachment(s)
            </span>
          )}
        </div>

        {/* Search Bar */}
        <div className="relative">
          <Search className="w-4 h-4 text-surface-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search chat or person..."
            className="w-full pl-9 pr-3 py-2 bg-surface-50 dark:bg-surface-800 border border-surface-200 dark:border-surface-700 rounded-xl text-xs text-surface-900 dark:text-surface-100 placeholder-surface-400 focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500"
          />
        </div>

        {/* Channel Selection List */}
        <div className="max-h-60 overflow-y-auto space-y-1 pr-1">
          {filteredChannels.length === 0 ? (
            <div className="py-6 text-center text-xs text-surface-400">No chats found.</div>
          ) : (
            filteredChannels.map((c) => {
              const isSelected = selectedChannelId === c.id;
              const isDM = c.type === 'direct';
              const otherUser = isDM ? (c.members || []).find((m) => m?.id !== currentUser?.id) : null;
              const displayName = otherUser?.fullName || c.name;

              return (
                <button
                  key={c.id}
                  type="button"
                  onClick={() => setSelectedChannelId(c.id)}
                  className={`w-full flex items-center justify-between p-2 rounded-xl text-xs transition-colors ${
                    isSelected
                      ? 'bg-brand-50 dark:bg-brand-950/40 border border-brand-500/40 text-brand-700 dark:text-brand-300'
                      : 'hover:bg-surface-100 dark:hover:bg-surface-800 text-surface-800 dark:text-surface-200 border border-transparent'
                  }`}
                >
                  <div className="flex items-center space-x-2.5 truncate">
                    {isDM ? (
                      <Avatar src={otherUser?.avatarUrl} name={displayName} size="xs" />
                    ) : (
                      <div className="w-6 h-6 rounded-lg bg-brand-500/10 text-brand-600 dark:text-brand-400 flex items-center justify-center shrink-0">
                        <Users className="w-3.5 h-3.5" />
                      </div>
                    )}
                    <span className="font-semibold truncate">{displayName}</span>
                  </div>

                  {isSelected && (
                    <div className="w-5 h-5 rounded-full bg-brand-600 text-white flex items-center justify-center shrink-0">
                      <Check className="w-3 h-3 stroke-[3]" />
                    </div>
                  )}
                </button>
              );
            })
          )}
        </div>

        {error && (
          <div role="alert" className="p-2.5 rounded-xl bg-rose-50 dark:bg-rose-950/30 border border-rose-200 dark:border-rose-900 text-xs font-semibold text-rose-700 dark:text-rose-300">
            {error}
          </div>
        )}

        {/* Action Buttons */}
        <div className="pt-3 border-t border-surface-200 dark:border-surface-800 flex items-center justify-end space-x-2">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 font-medium text-xs text-surface-600 dark:text-surface-400 hover:bg-surface-100 dark:hover:bg-surface-800 rounded-xl"
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={!selectedChannelId || isSubmitting}
            onClick={handleSend}
            className="px-5 py-2 font-bold text-xs bg-brand-600 hover:bg-brand-700 disabled:opacity-40 text-white rounded-xl shadow-sm flex items-center space-x-1.5 transition-all"
          >
            <Send className="w-3.5 h-3.5" />
            <span>Forward</span>
          </button>
        </div>
      </div>
    </Modal>
  );
};
